import * as React from 'react';
import _ from 'lodash';
import ReactEcharts from 'echarts-for-react';
import { capitalizeAndKeepTheRest } from 'helpers/helpers';
import { formatDisplay } from 'helpers/formatHelper';
import { getLegendWidth } from 'helpers/chart/legendHelpers';
import { SerieT, ThreeDimGraphProps } from '../types';
import { getColorsScale } from './helpers/getColor';
import { getUnNestedDataFillingTranslationTree } from 'helpers/eChartHelpers';

const Treemap: React.FC<ThreeDimGraphProps> = ({
    settings,
    data,
    height,
    width,
    orderedColumns,
    isPdfModeActivated,
    isPngModeActivated,
    onClick,
}) => {
    const { x, y, z } = orderedColumns;
    const onChartClick = (point: {
        name: string | number;
        treePathInfo: Array<{ name: string | number }>;
        data: { name: string | number; serie?: string | number; value: number };
    }) => {
        const xValue = point.name;
        if (xValue && point.data.serie) {
            const zValue = point.data.serie;
            onClick({ [x.key]: xValue, [z.key]: zValue });
        }
    };

    const { showLegend, showLegendOnRightSide } = settings;
    const isExportModeActivated = isPdfModeActivated || isPngModeActivated;

    const colors = getColorsScale(settings?.palette);

    const xKey = x.key;
    const yKey = y.key;
    const zKey = z.key;

    const baseLegendOptions = {
        show: showLegend,
        selectedMode: false,
        itemWidth: 14,
        type: isExportModeActivated ? 'plain' : 'scroll',
        orient: 'horizontal',
        left: 'center',
        right: 'auto',
        top: 'auto',
        tooltip: {
            show: false,
        },
        formatter(name: string) {
            return capitalizeAndKeepTheRest(name);
        },
    };

    const rightSideLegendOptions = {
        orient: 'vertical',
        right: 2,
        left: 'auto',
        top: 'center',
        tooltip: {
            show: true,
            position: 'bottom',
        },
        textStyle: {
            overflow: 'truncate',
            lineHeight: 16,
            width: getLegendWidth(width),
        },
    };

    const option = React.useMemo(() => {
        const series: SerieT[] = [];
        const treemapData: SerieT[] = [];
        const treeValueTranslationsPerField: Record<
            string,
            Record<string, string>
        > = {};
        const unNestedData = getUnNestedDataFillingTranslationTree(
            data,
            treeValueTranslationsPerField,
        );

        const groupedData = _.groupBy(unNestedData, (d) => d[zKey]);
        const sortedData = _.sortBy(Object.entries(groupedData), [
            ([zValue]) => zValue,
        ]);
        const translateTreeData = (d: ResponseDataT, key: string) =>
            key in treeValueTranslationsPerField
                ? treeValueTranslationsPerField[key][d[key] as string]
                : d[key];

        sortedData.forEach(([zValue, children], index) => {
            // We build dummy series because legends are based on the number of series
            const zTransValue =
                zKey in treeValueTranslationsPerField
                    ? treeValueTranslationsPerField[zKey][zValue]
                    : zValue;
            series.push({
                type: 'treemap',
                right: showLegendOnRightSide ? 90 + getLegendWidth(width) : 20,
                left: 20,
                top: showLegendOnRightSide ? 20 : 40,
                bottom: 40,
                width: showLegendOnRightSide
                    ? width - (20 + getLegendWidth(width))
                    : width - 40,
                height: showLegendOnRightSide ? height - 60 : height - 80,
                nodeClick: false,
                roam: false,
                breadcrumb: {
                    show: false,
                },
                id: zTransValue,
                name: formatDisplay(zTransValue, z.type),
                // @ts-expect-error TODO fix label
                label: {
                    formatter(point: { name: string; value: unknown }) {
                        return `${point.name} : ${point.value}`;
                    },
                },
            });

            // Here is the real data, that will be displayed by the first serie.
            treemapData.push({
                name: zTransValue,
                // @ts-expect-error TODO fix label
                label: {
                    formatter: ({
                        name,
                        value,
                    }: {
                        name: string;
                        value: number;
                    }) => `${formatDisplay(name, z.type)} : ${value}`,
                },
                itemStyle: {
                    color: colors[index % colors.length],
                },
                children: children.map((d) => ({
                    name: translateTreeData(d, xKey),
                    label: {
                        formatter: ({
                            name,
                            value,
                        }: {
                            name: string;
                            value: number;
                        }) => `${formatDisplay(name, z.type)} : ${value}`,
                    },
                    serie: zValue,
                    value: translateTreeData(d, yKey),
                })),
            });
        });
        // @ts-expect-error [TS migration] (previously $FlowFixMe)
        series[0].data = treemapData;

        return {
            animation: false,
            color: colors,
            tooltip: {
                trigger: 'item',
                formatter(point: {
                    name: string | number;
                    value: number;
                    data: { serie: string };
                }) {
                    const serieName = formatDisplay(
                        treeValueTranslationsPerField[zKey][point.data.serie],
                        z.type,
                    );
                    const value = formatDisplay(
                        point.value,
                        y.type,
                        0, // isExtended
                    );
                    const pointName = formatDisplay(point.name, x.type);
                    return `<div>${serieName}</div>
                    <div>${pointName} : ${value}</div>`;
                },
            },
            legend: showLegendOnRightSide
                ? { ...baseLegendOptions, ...rightSideLegendOptions }
                : baseLegendOptions,
            series,
        };
    }, [data, settings, orderedColumns]);

    return (
        <ReactEcharts
            option={option}
            onEvents={{
                click: onChartClick,
            }}
            style={{ height, width }}
            className="treemap-chart"
            opts={{ renderer: 'canvas' }}
        />
    );
};

export default Treemap;
